import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { SkatteverketReceiptVATRequirementsUrl } from "utils/constants/receiptConstants";
import { SnackbarKey } from "notistack";
//#endregion
//#region components
import { Box } from "@mui/material";
import { ActionLinkButton, Icon, useFloatingMessages } from "components/common";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import useHighReceiptAmountWarning from "./useHighReceiptAmountWarning";
import cssExports from "theme/styles/main.module.scss";

interface HighReceiptAmountFloatingMessageProps {
  amount?: number;
  currency?: string;
  categoryType?: number;
  receiptCategoryId?: number;
  date?: Date;
}

const HighReceiptAmountFloatingMessage: React.FC<HighReceiptAmountFloatingMessageProps> = ({
  amount,
  currency,
  categoryType,
  receiptCategoryId,
  date,
}) => {
  const { enqueueMessage, closeMessage } = useFloatingMessages();

  const resolvedCategoryType = useSelector((store: AppState) => {
    if (categoryType !== undefined) return categoryType;
    if (receiptCategoryId === undefined) return undefined;
    const match = store.auth.selectedCompany.receiptCategories?.find((c) => c.id === receiptCategoryId);
    return match?.type;
  });

  const hasCategory = categoryType !== undefined || receiptCategoryId !== undefined;
  const shouldShow = useHighReceiptAmountWarning(amount, currency, resolvedCategoryType, hasCategory, date);

  const messageKeyRef = React.useRef<SnackbarKey | undefined>(undefined);

  React.useEffect(() => {
    if (shouldShow && messageKeyRef.current === undefined) {
      const openSkatteverket = () =>
        window.open(SkatteverketReceiptVATRequirementsUrl, "_blank", "noopener,noreferrer");
      const messageContent = (
        <Box>
          <Box>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.HighAmountVatWarning)}
          </Box>
          <Box mt={1}>
            <ActionLinkButton className={cssExports.animatedLink} onClick={openSkatteverket}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.HighAmountVatWarningLearnMore)}
              <Icon icon={IconDefinitionConstants.WArrowSmallRight} displayType={StyleEnums.DisplayType.Info} />
            </ActionLinkButton>
          </Box>
        </Box>
      );

      messageKeyRef.current = enqueueMessage(messageContent, StyleEnums.DisplayType.Info, false, true);
    } else if (!shouldShow && messageKeyRef.current !== undefined) {
      closeMessage(messageKeyRef.current);
      messageKeyRef.current = undefined;
    }
  }, [shouldShow, enqueueMessage, closeMessage]);

  React.useEffect(
    () => () => {
      if (messageKeyRef.current !== undefined) {
        closeMessage(messageKeyRef.current);
      }
    },
    [closeMessage]
  );

  return null;
};

export default HighReceiptAmountFloatingMessage;
